import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/storage';
import SuperAdminLayout from '../components/SuperAdminLayout';
import CollapsibleTable from '../components/CollapsibleTable';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Key, Mail, Trash2, Edit2, Eye, Clock, X } from 'lucide-react';
import confirm from '../utils/confirm';
import { useTranslation } from 'react-i18next';
import { Switch } from '../components/ui/switch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to format date without seconds
const formatDateTime = (isoString) => {
  if (!isoString) return '-';
  const date = new Date(isoString);
  return date.toLocaleString('no-NO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Login History Popup Component
const LoginHistoryPopup = ({ user, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const token = getToken();
        const response = await axios.get(`${API}/admin/users/${user.id}/login-history`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHistory(response.data);
      } catch (error) {
        console.error('Could not load login history:', error);
        toast.error('Kunne ikke laste innloggingshistorikk');
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [user.id]);

  return (
    <>
      <div 
        className="modal-overlay" 
        onClick={onClose}
        style={{ zIndex: 1000 }}
      />
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: '#1a1a1a',
          borderRadius: '12px',
          padding: '1.5rem',
          width: '90%',
          maxWidth: '500px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1001,
          border: '1px solid rgba(80, 227, 194, 0.3)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
        }}
      >
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '1rem',
          paddingBottom: '1rem',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}>
          <div>
            <h3 style={{ margin: 0, color: '#50E3C2', fontSize: '1.1rem' }}>
              <Clock size={18} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
              Innloggingshistorikk
            </h3>
            <p style={{ margin: '0.25rem 0 0 0', color: '#aaa', fontSize: '0.9rem' }}>
              {user.email}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#aaa',
              cursor: 'pointer',
              padding: '0.5rem'
            }}
          >
            <X size={24} />
          </button>
        </div>

        <div style={{ 
          flex: 1, 
          overflowY: 'auto',
          minHeight: '200px'
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#aaa' }}>
              Laster...
            </div>
          ) : history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#aaa' }}>
              Ingen innlogginger registrert
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {history.map((entry, index) => (
                <div 
                  key={index}
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    padding: '0.75rem 1rem',
                    borderRadius: '8px',
                    borderLeft: index === 0 ? '3px solid #50E3C2' : '3px solid transparent'
                  }}
                >
                  <div style={{ 
                    color: index === 0 ? '#50E3C2' : '#fff',
                    fontWeight: index === 0 ? '600' : '400',
                    fontSize: '0.95rem'
                  }}>
                    {formatDateTime(entry.login_at)}
                    {index === 0 && (
                      <span style={{ 
                        marginLeft: '0.5rem', 
                        fontSize: '0.75rem', 
                        background: '#50E3C2', 
                        color: '#000',
                        padding: '0.15rem 0.5rem',
                        borderRadius: '4px'
                      }}>
                        Siste
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const SuperAdminUsers = () => {
  const { t } = useTranslation();
  const [allUsers, setAllUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [showLoginHistory, setShowLoginHistory] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'customer_admin',
    organization_id: ''
  });

  useEffect(() => {
    fetchUsers();
    fetchOrganizations();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/admin/users/all-with-login`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllUsers(response.data);
    } catch (error) {
      toast.error('Kunne ikke laste brukere');
    }
  };

  const fetchOrganizations = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/admin/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrganizations(response.data);
    } catch (error) {
      console.error('Failed to fetch organizations');
    }
  };

  // Separate super admins and customer users
  const superAdmins = allUsers.filter(u => u.role === 'super_admin');
  const customerUsers = allUsers.filter(u => u.role !== 'super_admin');

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const token = getToken();
      
      if (isEditMode && selectedUser) {
        // Update existing user
        const updateData = {
          email: formData.email,
          role: formData.role,
          organization_id: formData.role === 'customer_admin' ? formData.organization_id : null
        };
        
        await axios.put(`${API}/admin/users/${selectedUser.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Bruker oppdatert!');
      } else {
        // Create new user
        const createData = {
          email: formData.email,
          password: formData.password,
          role: formData.role,
          organization_id: formData.role === 'customer_admin' && formData.organization_id ? formData.organization_id : null
        };
        
        await axios.post(`${API}/admin/users`, createData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Bruker opprettet!');
      }
      
      setShowModal(false);
      setIsEditMode(false);
      setSelectedUser(null);
      fetchUsers();
      setFormData({
        email: '',
        password: '',
        role: 'customer_admin',
        organization_id: ''
      });
    } catch (error) {
      toast.error(isEditMode ? 'Kunne ikke oppdatere bruker' : 'Kunne ikke opprette bruker');
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsEditMode(true);
    setFormData({
      email: user.email,
      password: '',
      role: user.role,
      organization_id: user.organization_id || ''
    });
    setShowModal(true);
  };

  const handleChangePassword = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = getToken();
      await axios.put(`${API}/admin/users/${selectedUser.id}/password`, 
        { password: newPassword },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Passord endret!');
      setShowPasswordModal(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error('Kunne ikke endre passord');
    }
  };

  const handleSendPasswordReset = async (user) => {
    if (!await confirm.info(`Send passord-reset lenke til ${user.email}?`)) return;
    
    try {
      const token = getToken();
      await axios.post(`${API}/admin/users/${user.id}/send-password-reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Passord-reset lenke sendt!');
    } catch (error) {
      toast.error('Kunne ikke sende passord-reset lenke');
    }
  };

  const handleDeleteUser = async (user) => {
    if (!await confirm.danger(`Er du sikker på at du vil slette brukeren ${user.email}?`)) return;
    
    try {
      const token = getToken();
      await axios.delete(`${API}/admin/users/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bruker slettet!');
      fetchUsers();
    } catch (error) {
      toast.error('Kunne ikke slette bruker');
    }
  };

  const toggleDemoUser = async (user) => {
    try {
      const token = getToken();
      await axios.post(`${API}/admin/users/${user.id}/toggle-demo-user`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(user.is_demo_user ? 'Bruker er ikke lenger demo-bruker' : 'Bruker satt som demo-bruker');
      fetchUsers();
    } catch (error) {
      console.error('Failed to toggle demo user:', error);
      toast.error('Kunne ikke endre demo-bruker status');
    }
  };

  // Table columns for super admins
  const superAdminColumns = [
    {
      key: 'email',
      label: t('superAdmin.users.email'),
      minWidth: '200px'
    },
    {
      key: 'role',
      label: t('superAdmin.users.role'),
      minWidth: '120px',
      render: () => (
        <span className="badge badge-danger">Super Admin</span>
      )
    },
    {
      key: 'last_login',
      label: 'Sist innlogget',
      minWidth: '140px',
      render: (user) => (
        <span style={{ color: user.last_login ? '#fff' : '#666', fontSize: '0.9rem' }}>
          {formatDateTime(user.last_login)}
        </span>
      )
    },
    {
      key: 'login_history',
      label: 'Logg',
      minWidth: '60px',
      render: (user) => (
        <button
          className="btn btn-secondary"
          style={{ padding: '0.4rem 0.6rem', fontSize: '0.8rem' }}
          onClick={() => setShowLoginHistory(user)}
          title="Vis innloggingshistorikk"
        >
          <Clock size={16} />
        </button>
      )
    },
    {
      key: 'is_active',
      label: t('common.status'),
      minWidth: '90px',
      render: (user) => (
        <span className={user.is_active ? 'badge badge-success' : 'badge badge-danger'}>
          {user.is_active ? t('common.active') : t('common.inactive')}
        </span>
      )
    }
  ];

  // Table columns for customer users
  const customerUserColumns = [
    {
      key: 'email',
      label: t('superAdmin.users.email'),
      minWidth: '180px'
    },
    {
      key: 'organization_name',
      label: 'Organisasjon',
      minWidth: '150px',
      render: (user) => (
        <span style={{ color: user.organization_name !== '-' ? '#fff' : '#666' }}>
          {user.organization_name}
        </span>
      )
    },
    {
      key: 'last_login',
      label: 'Sist innlogget',
      minWidth: '140px',
      render: (user) => (
        <span style={{ color: user.last_login ? '#fff' : '#666', fontSize: '0.9rem' }}>
          {formatDateTime(user.last_login)}
        </span>
      )
    },
    {
      key: 'login_history',
      label: 'Logg',
      minWidth: '60px',
      render: (user) => (
        <button
          className="btn btn-secondary"
          style={{ padding: '0.4rem 0.6rem', fontSize: '0.8rem' }}
          onClick={() => setShowLoginHistory(user)}
          title="Vis innloggingshistorikk"
        >
          <Clock size={16} />
        </button>
      )
    },
    {
      key: 'is_demo_user',
      label: 'Demo',
      minWidth: '80px',
      render: (user) => {
        if (!user.is_open_demo_org) {
          return <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>-</span>;
        }
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Switch
              checked={user.is_demo_user || false}
              onCheckedChange={() => toggleDemoUser(user)}
              style={{ backgroundColor: user.is_demo_user ? '#f59e0b' : '#6b7280' }}
            />
          </div>
        );
      }
    },
    {
      key: 'is_active',
      label: t('common.status'),
      minWidth: '80px',
      render: (user) => (
        <span className={user.is_active ? 'badge badge-success' : 'badge badge-danger'}>
          {user.is_active ? t('common.active') : t('common.inactive')}
        </span>
      )
    }
  ];

  const userActions = (user) => (
    <>
      <button 
        className="btn btn-primary" 
        style={{ fontSize: '0.8rem', padding: 'clamp(0.4rem, 1vw, 0.5rem) 0.75rem' }}
        onClick={() => handleEditUser(user)}
        title={t('superAdmin.users.edit')}
      >
        <Edit2 size={window.innerWidth < 1280 ? 12 : 14} />
      </button>
      <button 
        className="btn btn-secondary" 
        style={{ fontSize: '0.8rem', padding: 'clamp(0.4rem, 1vw, 0.5rem) 0.75rem' }}
        onClick={() => handleChangePassword(user)}
        title={t('superAdmin.users.changePassword')}
      >
        <Key size={window.innerWidth < 1280 ? 12 : 14} />
      </button>
      <button 
        className="btn btn-secondary" 
        style={{ fontSize: '0.8rem', padding: 'clamp(0.4rem, 1vw, 0.5rem) 0.75rem' }}
        onClick={() => handleSendPasswordReset(user)}
        title={t('superAdmin.users.sendPasswordReset')}
      >
        <Mail size={window.innerWidth < 1280 ? 12 : 14} />
      </button>
      <button 
        className="btn btn-danger" 
        style={{ fontSize: '0.8rem', padding: 'clamp(0.4rem, 1vw, 0.5rem) 0.75rem' }}
        onClick={() => handleDeleteUser(user)}
        title="Slett bruker"
      >
        <Trash2 size={window.innerWidth < 1280 ? 12 : 14} />
      </button>
    </>
  );

  return (
    <SuperAdminLayout>
      <div className="admin-header">
        <h1>{t('superAdmin.users.title')}</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={20} /> {t('superAdmin.users.newUser')}
        </button>
      </div>

      {/* Super Admins Section */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ 
          color: '#50E3C2', 
          fontSize: '1.1rem', 
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          🛡️ Super-administratorer ({superAdmins.length})
        </h2>
        <CollapsibleTable
          columns={superAdminColumns}
          data={superAdmins}
          actions={userActions}
        />
      </div>

      {/* Customer Users Section */}
      <div>
        <h2 style={{ 
          color: '#50E3C2', 
          fontSize: '1.1rem', 
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          👥 Kundebrukere ({customerUsers.length})
        </h2>
        <CollapsibleTable
          columns={customerUserColumns}
          data={customerUsers}
          actions={userActions}
        />
      </div>

      {/* Login History Popup */}
      {showLoginHistory && (
        <LoginHistoryPopup 
          user={showLoginHistory} 
          onClose={() => setShowLoginHistory(null)} 
        />
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => {
          setShowModal(false);
          setIsEditMode(false);
          setSelectedUser(null);
          setFormData({
            email: '',
            password: '',
            role: 'customer_admin',
            organization_id: ''
          });
        }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{isEditMode ? t('superAdmin.users.editUser') : t('superAdmin.users.newUser')}</h2>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>{t('superAdmin.users.email')}</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>
              
              {!isEditMode && (
                <div className="form-group">
                  <label>{t('superAdmin.users.password')}</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required
                  />
                </div>
              )}

              <div className="form-group">
                <label>{t('superAdmin.users.role')}</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                >
                  <option value="customer_admin">Kundeadmin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>

              {formData.role === 'customer_admin' && (
                <div className="form-group">
                  <label>{t('superAdmin.dashboard.organization')}</label>
                  <select
                    value={formData.organization_id}
                    onChange={(e) => setFormData({...formData, organization_id: e.target.value})}
                    required
                  >
                    <option value="">{t('superAdmin.selectCustomer', {count: 0})}</option>
                    {organizations.map(org => (
                      <option key={org.id || org._id} value={org.id || org._id}>{org.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {isEditMode && (
                <div style={{ 
                  padding: '0.75rem', 
                  background: '#f0f9ff', 
                  border: '1px solid #bae6fd',
                  borderRadius: '8px',
                  marginTop: '1rem',
                  fontSize: '0.9rem',
                  color: '#0369a1'
                }}>
                  💡 {t('superAdmin.users.leaveBlankToKeep')}
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button type="submit" className="btn btn-primary">
                  {isEditMode ? t('common.update') : t('common.create')}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowModal(false);
                  setIsEditMode(false);
                  setSelectedUser(null);
                  setFormData({
                    email: '',
                    password: '',
                    role: 'customer_admin',
                    organization_id: ''
                  });
                }}>{t('common.cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {showPasswordModal && (
        <>
          <div className="modal-overlay" onClick={() => {
            setShowPasswordModal(false);
            setSelectedUser(null);
            setNewPassword('');
          }}></div>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{t('superAdmin.users.changePassword')} {selectedUser?.email}</h2>
            <form onSubmit={handlePasswordSubmit}>
              <div className="form-group">
                <label>{t('superAdmin.users.newPassword')}</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder={t('superAdmin.users.newPassword')}
                />
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button type="submit" className="btn btn-primary">{t('superAdmin.users.changePassword')}</button>
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowPasswordModal(false);
                  setSelectedUser(null);
                  setNewPassword('');
                }}>{t('common.cancel')}</button>
              </div>
            </form>
          </div>
        </>
      )}
    </SuperAdminLayout>
  );
};

export default SuperAdminUsers;
